import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, filter, map, Observable, of, shareReplay, switchMap, take, tap, } from 'rxjs';
import { environment } from 'src/environments/environment';
import { OrganizationService } from 'src/app/@shared';
import { v4 as uuidv4 } from 'uuid';
import { DEFAULT_PAGING } from 'src/app/@shared/constants/site.constants';
import { Filter } from 'src/app/@shared/models/filter.model';
import { Asset, EventAssetsService, OfferAsset, ProductAssetsService } from '..';
import * as dayjs from 'dayjs';

@Injectable({
  providedIn: 'root'
})

export class AssetsService<T extends Asset> {

  // initialize behavior subjects
  private pageBehaviorSubject = new BehaviorSubject(DEFAULT_PAGING);
  private loadingBehaviorSubject = new BehaviorSubject<boolean>(false);
  private searchBehaviorSubject = new BehaviorSubject<string>('');
  private sortBehaviorSubject = new BehaviorSubject({ active: 'DateAdded', direction: 'desc', });
  private reloadBehaviorSubject = new BehaviorSubject<string>('');
  private dialogReloadBehaviorSubject = new BehaviorSubject<string>('');
  private viewModeBehaviorSubject = new BehaviorSubject<string>('CARDS');
  private assetIdBehaviorSubject = new BehaviorSubject<string>('');
  private offerIdBehaviorSubject = new BehaviorSubject<string>('');
  private promoIdBehaviorSubject = new BehaviorSubject<string>('');
  private dialogSearchBehaviorSubject = new BehaviorSubject<string>('');
  private assetSearchBehaviorSubject = new BehaviorSubject<string>('');
  private assetPromoSearchBehaviorSubject = new BehaviorSubject<string>('');
  private offerGrpAPageBehaviorSubject = new BehaviorSubject(DEFAULT_PAGING);
  private offerGrpASearchBehaviorSubject = new BehaviorSubject<string>('');
  private offerGrpASortBehaviorSubject = new BehaviorSubject({ active: 'Rank', direction: 'asc', });
  private offerGrpAReloadBehaviorSubject = new BehaviorSubject<string>('');
  private offerGrpAtotalRecordsCount = new BehaviorSubject<number>(0);

  // we do not wish to expose our behavior subjects.  create public observables
  public page$ = this.pageBehaviorSubject.asObservable();
  public search$ = this.searchBehaviorSubject.asObservable();
  public sort$ = this.sortBehaviorSubject.asObservable();
  public isLoading$ = this.loadingBehaviorSubject.asObservable();
  public viewMode$ = this.viewModeBehaviorSubject.asObservable();
  public assetId$ = this.assetIdBehaviorSubject.asObservable();
  public offerId$ = this.offerIdBehaviorSubject.asObservable();
  public promoId$ = this.promoIdBehaviorSubject.asObservable();
  public dialogSearch$ = this.dialogSearchBehaviorSubject.asObservable();
  public assetSearch$ = this.assetSearchBehaviorSubject.asObservable();
  public assetPromoSearch$ = this.assetPromoSearchBehaviorSubject.asObservable();
  public offerGrpPage$ = this.offerGrpAPageBehaviorSubject.asObservable();
  public offerGrpSearch$ = this.offerGrpASearchBehaviorSubject.asObservable();
  public offerGrpSort$ = this.offerGrpASortBehaviorSubject.asObservable();
  public offerGrptotalRecordsCount$ = this.offerGrpAtotalRecordsCount.asObservable();

  constructor(private httpClient: HttpClient, private organizationService: OrganizationService, private paService: ProductAssetsService<OfferAsset>, private eventAssetService: EventAssetsService<OfferAsset>) { }

  // create the parameters observable that looks for changes in page, startDate, endDate, etc
  public params$ = combineLatest([
    this.assetIdBehaviorSubject,
    this.pageBehaviorSubject, // add debounce if we need to wait for user input ex: .pipe(debounceTime(300)), 
    this.sortBehaviorSubject,
    this.searchBehaviorSubject.pipe(debounceTime(300)),
    this.reloadBehaviorSubject,
  ]).pipe(
    distinctUntilChanged((previous, current) => {
      // if the values coming down this pipe are the same, don't continue the pipe
      return JSON.stringify(previous) === JSON.stringify(current);
    }),
    map(([assetId, page, sort, search, reload]) => {
      let _orderby = `Detail/${sort.active} ${sort.direction}`;
      if (sort.active == 'AssetGroupName') {
        _orderby = `${sort.active} ${sort.direction}`;
      }

      // set the query string parameters
      let params: HttpParams = new HttpParams({
        fromObject: {
          $skip: page.pageIndex * page.pageSize,
          $top: page.pageSize,
          $orderby: _orderby,
          $count: true,
          $expand: 'Detail'
        }
      });

      // if there is a search, add the search to the parameters
      if (search.length) {
        params = params.append('$search', `"${search}"`);
      }

      return params;
    })
  );

  public dialogParams$ = combineLatest([
    this.assetIdBehaviorSubject,
    this.pageBehaviorSubject, // add debounce if we need to wait for user input ex: .pipe(debounceTime(300)), 
    this.sortBehaviorSubject,
    this.dialogSearchBehaviorSubject.pipe(debounceTime(300)),
    this.dialogReloadBehaviorSubject,
  ]).pipe(
    distinctUntilChanged((previous, current) => {
      // if the values coming down this pipe are the same, don't continue the pipe
      return JSON.stringify(previous) === JSON.stringify(current);
    }),
    map(([assetId, page, sort, search, reload]) => {

      // set the query string odata parameters
      let params: HttpParams = new HttpParams({
        fromObject: {
          assetId: this.assetIdBehaviorSubject.value,
          $skip: page.pageIndex * page.pageSize,
          $top: page.pageSize,
          $orderby: `${sort.active} ${sort.direction}`,
          $expand: 'Detail',
          $count: true,
        }
      });

      // if there is a search, add the search to the parameters
      if (search.length) {
        params = params.append('$search', `"${search}"`);
      }

      return params;
    })
  );

  public assetsByNotProductIdParams$ = combineLatest([
    this.paService.productId$,
    this.pageBehaviorSubject, // add debounce if we need to wait for user input ex: .pipe(debounceTime(300)),
    this.sortBehaviorSubject,
    this.dialogSearchBehaviorSubject.pipe(debounceTime(300)),
    this.reloadBehaviorSubject,
  ]).pipe(
    distinctUntilChanged((previous, current) => {
      // if the values coming down this pipe are the same, don't continue the pipe
      return JSON.stringify(previous) === JSON.stringify(current);
    }),
    map(([productId, page, sort, search, reload]) => {
      // set the query string odata parameters
      let params: HttpParams = new HttpParams({
        fromObject: {
          productId,
          $skip: page.pageIndex * page.pageSize,
          $top: page.pageSize,
          $orderby: `${sort.active} ${sort.direction}`,
          $expand: 'Detail',
          $count: true,
        },
      });

      // if there is a search, add the search to the parameters
      if (search.length) {
        params = params.append('$search', `"${search}"`);
      }

      return params;
    })
  );

  public eventAssetdialogParams$ = combineLatest([
    this.assetIdBehaviorSubject,
    this.pageBehaviorSubject, // add debounce if we need to wait for user input ex: .pipe(debounceTime(300)), 
    this.sortBehaviorSubject,
    this.dialogSearchBehaviorSubject.pipe(debounceTime(300)),
    this.reloadBehaviorSubject,
  ]).pipe(
    distinctUntilChanged((previous, current) => {
      // if the values coming down this pipe are the same, don't continue the pipe
      return JSON.stringify(previous) === JSON.stringify(current);
    }),
    map(([assetId, page, sort, search, reload]) => {

      // set the query string odata parameters
      let params: HttpParams = new HttpParams({
        fromObject: {
          assetId: this.assetIdBehaviorSubject.value,
          $skip: page.pageIndex * page.pageSize,
          $top: page.pageSize,
          $orderby: `${sort.active} ${sort.direction}`,
          $expand: 'Detail',
          $count: true,
        }
      });

      // if there is a search, add the search to the parameters
      if (search.length) {
        params = params.append('$search', `"${search}"`);
      }

      return params;
    })
  );

  public assetsByNotEventIdParams$ = combineLatest([
    this.eventAssetService.eventId$,
    this.pageBehaviorSubject, // add debounce if we need to wait for user input ex: .pipe(debounceTime(300)), 
    this.sortBehaviorSubject,
    this.dialogSearchBehaviorSubject.pipe(debounceTime(300)),
    this.reloadBehaviorSubject,
  ]).pipe(
    distinctUntilChanged((previous, current) => {
      // if the values coming down this pipe are the same, don't continue the pipe
      return JSON.stringify(previous) === JSON.stringify(current);
    }),
    map(([eventId, page, sort, search, reload]) => {

      // set the query string odata parameters
      let params: HttpParams = new HttpParams({
        fromObject: {
          eventId,
          $skip: page.pageIndex * page.pageSize,
          $top: page.pageSize,
          $orderby: `${sort.active} ${sort.direction}`,
          $expand: 'Detail',
          $count: true,
        }
      });

      // if there is a search, add the search to the parameters
      if (search.length) {
        params = params.append('$search', `"${search}"`);
      }

      return params;
    })
  );

  public versionAssetdialogParams$ = combineLatest([
    this.assetIdBehaviorSubject,
    this.pageBehaviorSubject, // add debounce if we need to wait for user input ex: .pipe(debounceTime(300)), 
    this.sortBehaviorSubject,
    this.dialogSearchBehaviorSubject.pipe(debounceTime(300)),
    this.reloadBehaviorSubject,
  ]).pipe(
    distinctUntilChanged((previous, current) => {
      // if the values coming down this pipe are the same, don't continue the pipe
      return JSON.stringify(previous) === JSON.stringify(current);
    }),
    map(([assetId, page, sort, search, reload]) => {

      // set the query string odata parameters
      let params: HttpParams = new HttpParams({
        fromObject: {
          assetId: this.assetIdBehaviorSubject.value,
          $skip: page.pageIndex * page.pageSize,
          $top: page.pageSize,
          $orderby: `${sort.active} ${sort.direction}`,
          $expand: 'Detail',
          $count: true,
        }
      });

      // if there is a search, add the search to the parameters
      if (search.length) {
        params = params.append('$search', `"${search}"`);
      }

      return params;
    })
  );

  public dialogAssetParams$ = combineLatest([
    this.offerIdBehaviorSubject,
    this.pageBehaviorSubject, // add debounce if we need to wait for user input ex: .pipe(debounceTime(300)), 
    this.sortBehaviorSubject,
    this.assetSearchBehaviorSubject.pipe(debounceTime(300)),
    this.reloadBehaviorSubject,
  ]).pipe(
    distinctUntilChanged((previous, current) => {
      // if the values coming down this pipe are the same, don't continue the pipe
      return JSON.stringify(previous) === JSON.stringify(current);
    }),
    map(([offerId, page, sort, search, reload]) => {

      // set the query string odata parameters
      let params: HttpParams = new HttpParams({
        fromObject: {
          offerId: this.offerIdBehaviorSubject.value,
          $skip: page.pageIndex * page.pageSize,
          $top: page.pageSize,
          $orderby: `${sort.active} ${sort.direction}`,
          $expand: 'Detail',
          $count: true,
        }
      });

      // if there is a search, add the search to the parameters
      if (search.length) {
        params = params.append('$search', `"${search}"`);
      }

      return params;
    })
  );

  public dialogAssetPromoParams$ = combineLatest([
    this.promoIdBehaviorSubject,
    this.pageBehaviorSubject, // add debounce if we need to wait for user input ex: .pipe(debounceTime(300)), 
    this.sortBehaviorSubject,
    this.assetPromoSearchBehaviorSubject.pipe(debounceTime(300)),
    this.reloadBehaviorSubject,
  ]).pipe(
    distinctUntilChanged((previous, current) => {
      // if the values coming down this pipe are the same, don't continue the pipe
      return JSON.stringify(previous) === JSON.stringify(current);
    }),
    map(([promoId, page, sort, search, reload]) => {

      // set the query string odata parameters
      let params: HttpParams = new HttpParams({
        fromObject: {
          promoId: this.promoIdBehaviorSubject.value,
          $skip: page.pageIndex * page.pageSize,
          $top: page.pageSize,
          $orderby: `${sort.active} ${sort.direction}`,
          $expand: 'Detail',
          $count: true,
        }
      });

      // if there is a search, add the search to the parameters
      if (search.length) {
        params = params.append('$search', `"${search}"`);
      }

      return params;
    })
  );

  public offerGrpDAssetParams$ = combineLatest([
    this.offerIdBehaviorSubject,
    this.offerGrpAPageBehaviorSubject, // add debounce if we need to wait for user input ex: .pipe(debounceTime(300)), 
    this.offerGrpASortBehaviorSubject,
    this.offerGrpASearchBehaviorSubject.pipe(debounceTime(300)),
    this.offerGrpAReloadBehaviorSubject,
  ]).pipe(
    distinctUntilChanged((previous, current) => {
      // if the values coming down this pipe are the same, don't continue the pipe
      return JSON.stringify(previous) === JSON.stringify(current);
    }),
    map(([offerGroupId, page, sort, search, reload]) => {

      // set the query string odata parameters
      let params: HttpParams = new HttpParams({
        fromObject: {
          offerGroupId: this.offerIdBehaviorSubject.value,
          $skip: page.pageIndex * page.pageSize,
          $top: page.pageSize,
          $orderby: `${sort.active} ${sort.direction}`,
          $expand: 'Detail',
          $count: true,
        }
      });

      // if there is a search, add the search to the parameters
      if (search.length) {
        params = params.append('$search', `"${search}"`);
      }

      return params;
    })
  );

  set assetId(assetId: string) {
    this.assetIdBehaviorSubject.next(assetId);
  }
  set promoId(promoId: string) {
    this.promoIdBehaviorSubject.next(promoId);
  }
  set offerId(offerId: string) {
    this.offerIdBehaviorSubject.next(offerId);
  }
  // create the assets observable that calls http get when any of our parameters change
  private assetsResponse$ = this.params$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)), // set isLoading to true
    switchMap((_params) =>
      this.httpClient.get(`${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/AssetDomains`, { params: _params })
    ),
    tap(() => this.loadingBehaviorSubject.next(false)), // set isLoading to false
    shareReplay(1) // make sure all subscriptions share the same http call (otherwise there will be a http call for each subscription)
  );

  // create the assets observable that calls http get when any of our parameters change
  private allAssetsResponse$ = this.dialogParams$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)), // set isLoading to true
    switchMap((_params) =>
      this.httpClient.get(
        `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/AssetDomains`,
        { params: _params })
    ),
    tap(() => this.loadingBehaviorSubject.next(false)), // set isLoading to false
    shareReplay(1) // make sure all subscriptions share the same http call (otherwise there will be a http call for each subscription)
  );


  private allAssetsByNotProductIdResponse$ = this.assetsByNotProductIdParams$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)), // set isLoading to true
    switchMap((_params) => {
      if (_params.has('$search')) {
        return this.httpClient.get(
          `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/AssetDomains/GetAssetsByNotProductId`,
          { params: _params }
        );
      } else {
        return of([])
      }
    }),
    tap(() => this.loadingBehaviorSubject.next(false)), // set isLoading to false
    shareReplay(1) // make sure all subscriptions share the same http call (otherwise there will be a http call for each subscription)
  );

  // create the assets observable that calls http get when any of our parameters change
  private allAssetsForOffersResponse$ = this.dialogAssetParams$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)), // set isLoading to true
    switchMap((_params) => {
      if (_params.has('$search')) {
        return this.httpClient.get(
          `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/AssetDomains/GetAssetsByNotOfferId`,
          { params: _params })
      } else {
        return of([])
      }
    }),

    tap(() => this.loadingBehaviorSubject.next(false)), // set isLoading to false
    shareReplay(1) // make sure all subscriptions share the same http call (otherwise there will be a http call for each subscription)
  );


  // create the assets observable that calls http get when any of our parameters change
  private allAssetsForPromoResponse$ = this.dialogAssetPromoParams$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)), // set isLoading to true
    switchMap((_params) => {
      if (_params.has('$search')) {
        return this.httpClient.get(
          `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/AssetDomains/GetAssetsByNotPromoId`,
          { params: _params })
      } else {
        return of([])
      }
    }),
    tap(() => this.loadingBehaviorSubject.next(false)), // set isLoading to false
    shareReplay(1) // make sure all subscriptions share the same http call (otherwise there will be a http call for each subscription)
  );

  // create the assets observable that calls http get when any of our parameters change
  private allEventAssetsResponse$ = this.eventAssetdialogParams$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)), // set isLoading to true
    switchMap((_params) =>
      this.httpClient.get(
        `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/AssetDomains`,
        { params: _params })
    ),
    tap(() => this.loadingBehaviorSubject.next(false)), // set isLoading to false
    shareReplay(1) // make sure all subscriptions share the same http call (otherwise there will be a http call for each subscription)
  );

  private allAssetByNotEventIdResponse$ = this.assetsByNotEventIdParams$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)), // set isLoading to true
    switchMap((_params) => {
      if (_params.has('$search')) {
        return this.httpClient.get(
          `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/AssetDomains/GetAssetsByNotEventId`,
          { params: _params })
      } else {
        return of([])
      }
    }),
    tap(() => this.loadingBehaviorSubject.next(false)), // set isLoading to false
    shareReplay(1) // make sure all subscriptions share the same http call (otherwise there will be a http call for each subscription)
  );

  // create the assets observable that calls http get when any of our parameters change
  private allVersionAssetsResponse$ = this.versionAssetdialogParams$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)), // set isLoading to true
    switchMap((_params) =>
      this.httpClient.get(
        `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/AssetDomains`,
        { params: _params })
    ),
    tap(() => this.loadingBehaviorSubject.next(false)), // set isLoading to false
    shareReplay(1) // make sure all subscriptions share the same http call (otherwise there will be a http call for each subscription)
  );

  uploadAsset(formData: any) {
    return this.httpClient.post(
      `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/AssetDomains/Upload`,
      formData,
      { reportProgress: true, responseType: 'json' });
  }

  // assets listing
  public assets$: Observable<T[]> = this.assetsResponse$.pipe(
    map((res: any) => res.value)
  );

  // total number of offerTag records based on filtering
  public assetsTotalRecords$: Observable<number> = this.assetsResponse$.pipe(
    map((res: any) => res['@odata.count'])
  );

  // assets listing
  public allAssets$: Observable<T[]> = this.allAssetsByNotProductIdResponse$.pipe(
    map((res: any) => res.value)
  );


  public assetsByNotProductId$ = this.allAssetsByNotProductIdResponse$.pipe(
    map((res: any) => res.value)
  );

  // assets listing
  public allAssetsForOffers$: Observable<T[]> = this.allAssetsForOffersResponse$.pipe(
    map((res: any) => res.value)
  );

  public allAssetsForPromos$: Observable<T[]> = this.allAssetsForPromoResponse$.pipe(
    map((res: any) => res.value)
  );

  // assets listing
  public allEventAssets$: Observable<T[]> = this.allEventAssetsResponse$.pipe(
    map((res: any) => {
      return res.value;
    })
  );

  public allAssetsByNotEventId$: Observable<T[]> = this.allAssetByNotEventIdResponse$.pipe(
    map((res: any) => {
      return res.value;
    })
  );

  // assets listing
  public allVersionAssets$: Observable<T[]> = this.allVersionAssetsResponse$.pipe(
    map((res: any) => res.value)
  );

  deleteAsset(id: string) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/AssetDomains/${id}`;
    return this.httpClient.delete(url);
  }

  // total number of asset records based on filtering
  public totalRecords$: Observable<number> = this.assetsByNotProductIdParams$.pipe(
    map((res: any) => res['@odata.count'])
  );

  // total number of asset records based on filtering
  public allAssetsByNotEventIdTotalRecords$: Observable<number> =
    this.allAssetByNotEventIdResponse$.pipe(
      map((res: any) => {
        return res['@odata.count'];
      })
    );

  // total number of asset records based on filtering
  public assetTotalRecords$: Observable<number> = this.allAssetsByNotProductIdResponse$.pipe(
    map((res: any) => res['@odata.count'])
  );

  // total number of asset records based on filtering
  public allAssetsForOffersTotalRecords$: Observable<number> = this.allAssetsForOffersResponse$.pipe(
    map((res: any) => res['@odata.count'])
  );

  // total number of asset records based on filtering
  public assetsPromoTotalRecords$: Observable<number> = this.allAssetsForPromoResponse$.pipe(
    map((res: any) => res['@odata.count'])
  );


  // total number of asset records based on filtering
  public EventAssetTotalRecords$: Observable<number> = this.allEventAssetsResponse$.pipe(
    map((res: any) => res['@odata.count'])
  );

  // total number of asset records based on filtering
  public VersionAssetTotalRecords$: Observable<number> = this.allVersionAssetsResponse$.pipe(
    map((res: any) => res['@odata.count'])
  );

  // set the current page
  page(page: any) {
    this.pageBehaviorSubject.next(page);
  }

  toggleViewMode(mode: string) {
    this.viewModeBehaviorSubject.next(mode);
  }

  // sets the sort property and order
  sort(sort: any) {
    this.sortBehaviorSubject.next(sort);
  }

  // sets the search phrase
  search(search: string) {
    const page = this.pageBehaviorSubject.value;
    page.pageIndex = 0;
    page.previousPageIndex = 0;
    this.searchBehaviorSubject.next(search);
    this.pageBehaviorSubject.next(page);
  }

  // sets the search phrase
  dialogSearch(search: string) {
    const page = this.pageBehaviorSubject.value;
    page.pageIndex = 0;
    page.previousPageIndex = 0;
    this.dialogSearchBehaviorSubject.next(search);
    this.pageBehaviorSubject.next(page);
  }

  // sets the search phrase
  dialogAssetSearch(search: string) {
    const page = this.pageBehaviorSubject.value;
    page.pageIndex = 0;
    page.previousPageIndex = 0;
    this.assetSearchBehaviorSubject.next(search);
    this.pageBehaviorSubject.next(page);
  }

  // sets the search phrase
  dialogAssetPromoSearch(search: string) {
    const page = this.pageBehaviorSubject.value;
    page.pageIndex = 0;
    page.previousPageIndex = 0;
    this.assetPromoSearchBehaviorSubject.next(search);
    this.pageBehaviorSubject.next(page);
  }

  // reloads/refreshes the store listing
  reload() {
    // reload the Store data
    this.reloadBehaviorSubject.next(uuidv4());
  }

  // reset the current page
  resetpage() {
    this.pageBehaviorSubject.next(DEFAULT_PAGING);
  }

  // getListByFieldName for filters
  getListByFieldName(fieldName: string): Observable<T[]> {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/AssetDomains/Get?$apply=groupby((${fieldName}))`;
    return this.httpClient.get<T[]>(url);
  }

  downloadAssetsJSON() {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Assets/EtlExport`;
    window.open(url, '_blank');
  }

  downloadAssetsCSV() {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Assets/Export`;
    window.open(url, '_blank');
  }

  // create the assets observable that calls http get when any of our parameters change
  private offerGrpDialogAssetsResponse$ = this.offerGrpDAssetParams$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)), // set isLoading to true
    switchMap((_params) => {
      if (_params.has('$search')) {
        return this.httpClient.get(
          `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/AssetDomains/GetAssetsByNotOfferGroupId`,
          { params: _params })
      } else {
        return of([])
      }
    }),

    tap(() => this.loadingBehaviorSubject.next(false)), // set isLoading to false
    shareReplay(1) // make sure all subscriptions share the same http call (otherwise there will be a http call for each subscription)
  );

  // assets listing
  public offerGdialogAssets$: Observable<T[]> = this.offerGrpDialogAssetsResponse$.pipe(
    map((res: any) => res.value)
  );

  // total number of asset records based on filtering
  public offerGdialogAssetsTotalRecords$: Observable<number> = this.offerGrpDialogAssetsResponse$.pipe(
    map((res: any) => res['@odata.count'])
  );

  offerGrpPage(page: any) {
    this.offerGrpAPageBehaviorSubject.next(page);
  }

  // sets the sort property and order
  offerGrpsort(sort: any) {
    this.offerGrpASortBehaviorSubject.next(sort);
  }

  // sets the search phrase
  offerGrpsearch(search: string) {
    this.offerGrpASearchBehaviorSubject.next(search);
    this.offerGrpAPageBehaviorSubject.next(DEFAULT_PAGING);
  }

  // reloads/refreshes the store listing
  offerGrpreload() {
    // reload the Store data
    this.offerGrpAReloadBehaviorSubject.next(uuidv4());
  }

  reUpload(formData: any, key: string) {
    return this.httpClient.post(
      `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/AssetDomains/ReUpload?key=${key}`,
      formData,
      { reportProgress: true, responseType: 'json' });
  }
} 
